'use client';
import React from 'react';
import { CheckCircle, Lock, Play, Clock } from 'lucide-react';
import type { Level } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';

interface Props {
  level: Level;
  language: Language;
  isSelected: boolean;
  onSelect: () => void;
}

export default function LevelCard({ level, language, isSelected, onSelect }: Props) {
  const isCompleted = level.completionPercent === 100;
  const isInProgress = level.completionPercent > 0 && level.completionPercent < 100;
  const isLocked = !level.isUnlocked;

  return (
    <button
      onClick={onSelect}
      disabled={isLocked}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all duration-150 text-left group ${
        isSelected
          ? 'border-primary bg-primary/5 card-shadow-lg'
          : isLocked
          ? 'border-border bg-muted/30 cursor-not-allowed' :'border-border bg-card hover:border-primary/40 hover:bg-secondary card-shadow'
      } ${isInProgress ? 'pulse-ring' : ''}`}
    >
      {/* Icon */}
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 relative ${
          isLocked ? 'bg-muted' : ''
        }`}
        style={!isLocked ? { backgroundColor: `${level.color}20` } : {}}
      >
        {isLocked ? '🔒' : level.icon}
        {isCompleted && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-success rounded-full flex items-center justify-center">
            <CheckCircle size={12} className="text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-600 text-muted-foreground">
            {language === 'fr' ? `Niveau ${level.number}` : `Уровень ${level.number}`}
          </span>
          {isInProgress && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-600">
              {t(language, 'learning_in_progress')}
            </span>
          )}
          {isCompleted && (<span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-600">
              {t(language, 'learning_completed')}
            </span>
          )}
        </div>
        <p className={`text-sm font-600 truncate ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
          {level.title[language]}
        </p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{level.description[language]}</p>

        {/* Progress bar */}
        {!isLocked && (
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden w-full">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${level.completionPercent}%`, backgroundColor: level.color }}
            />
          </div>
        )}
      </div>

      {/* Right: lessons count + status icon */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {isLocked ? (
            <Lock size={14} />
          ) : isCompleted ? (
            <CheckCircle size={14} className="text-success" />
          ) : isInProgress ? (
            <Clock size={14} className="text-accent" />
          ) : (
            <Play size={14} className="text-primary" />
          )}
        </div>
        <span className="text-xs text-muted-foreground font-500">
          {level.lessons.length} {language === 'fr' ? 'leçons' : 'уроков'}
        </span>
        <span className="text-xs font-700" style={{ color: level.color }}>
          {level.completionPercent}%
        </span>
      </div>
    </button>
  );
}